import { getOrdinal } from "@circle-react/helpers/localeHelpers";
import type { PaywallCheckoutPreview } from "@circle-react/types";
import { compact } from "lodash";

import { t } from "@/i18n-js/instance";

import type { CheckoutPreviewEntryProps } from "../../../components/PreviewEntry";
import type { ComponentLineProps } from "../../FirstPaymentPreview/types";
import { shouldExpandFirstPayment } from "../../FirstPaymentPreview/variants/helpers";
import { RawLines } from "../RawLines";
import { getNumberOfInstallments } from "../variants/helpers";
import { shouldExpandFuturePayments } from "../variants/helpers";

const trialHeader = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => ({
  label: t(
    "paywall_checkout.future_payments.future_payment_after_x_trial_days",
    { count: preview?.trial_period?.days },
  ),
  value: shouldExpandFuturePayments(preview)
    ? preview.future_payments?.formatted_amount_due
    : undefined,
  valueWeight: "semibold",
});

const headerFuturePayments = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => ({
  label: t(
    "paywall_checkout.future_payments.header_installments.one_installment",
  ),
  value: shouldExpandFuturePayments(preview)
    ? preview.future_payments?.formatted_amount_due
    : undefined,
  valueWeight: "semibold",
});

const oneInstallmentStrategies = {
  trial_then_value: (preview: PaywallCheckoutPreview) => {
    const options = trialHeader(preview);

    return {
      ...options,
      value: shouldExpandFirstPayment(preview)
        ? preview.first_payment.formatted_amount_due
        : undefined,
    };
  },
  trial_then_upfront_then_value: trialHeader,
  upfront_then_trial_then_value: headerFuturePayments,
  upfront_then_value: headerFuturePayments,
  value: () => ({ hide: true }),
};

const headerForFistInstallment = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => {
  const numberOfInstallments = getNumberOfInstallments(preview);
  const labelString = compact([
    t([
      "paywall_checkout.future_payments.header_installments.first_payment",
      preview.paywall_price.interval_type,
    ]),
    `(${t("progress_counter", { count: getOrdinal(1), total_count: numberOfInstallments })})`,
  ]).join(" ");

  return {
    label: labelString,
    value: shouldExpandFuturePayments(preview)
      ? preview.future_payments?.formatted_amount_due
      : undefined,
  };
};

const multipleInstallmentsStrategies = {
  trial_then_value: (
    preview: PaywallCheckoutPreview,
  ): Partial<CheckoutPreviewEntryProps> => {
    const numberOfInstallments = getNumberOfInstallments(preview);

    return {
      label: compact([
        t([
          "paywall_checkout.future_payments.header_installments.first_payment",
          preview.paywall_price.interval_type,
        ]),
        `(${t("progress_counter", { count: getOrdinal(1), total_count: numberOfInstallments })})`,
      ]).join(" "),
      value: shouldExpandFirstPayment(preview)
        ? preview.first_payment?.formatted_amount_due
        : undefined,
    };
  },
  trial_then_upfront_then_value: headerForFistInstallment,
  upfront_then_trial_then_value: (
    preview: PaywallCheckoutPreview,
  ): Partial<CheckoutPreviewEntryProps> => {
    const numberOfInstallments = getNumberOfInstallments(preview);
    const labelString = compact([
      t([
        "paywall_checkout.future_payments.header_installments.first_payment",
        preview.paywall_price.interval_type,
      ]),
      `(${t("progress_counter", { count: getOrdinal(1), total_count: numberOfInstallments })})`,
    ]).join(" ");

    if (shouldExpandFuturePayments(preview)) {
      return {
        label: labelString,
        value: preview.future_payments?.formatted_amount_due,
        valueWeight: "semibold",
      };
    }

    return {
      label: labelString,
      value: undefined,
    };
  },
  upfront_then_value: headerForFistInstallment,
  value: (
    preview: PaywallCheckoutPreview,
  ): Partial<CheckoutPreviewEntryProps> => {
    const numberOfInstallments = getNumberOfInstallments(preview);
    const labelString = [
      t([
        "paywall_checkout.future_payments.header_installments.next_payment",
        preview.paywall_price.interval_type,
      ]),
      `(${t("progress_counter", { count: getOrdinal(2), total_count: numberOfInstallments })})`,
    ].join(" ");

    return {
      label: labelString,
      value: shouldExpandFuturePayments(preview)
        ? preview.future_payments?.formatted_amount_due
        : undefined,
    };
  },
};

const buildOptions = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => {
  if (getNumberOfInstallments(preview) === 1) {
    return oneInstallmentStrategies[preview.preview_scenario](preview);
  }

  return multipleInstallmentsStrategies[preview.preview_scenario](preview);
};

export const HeaderLine = ({
  preview,
}: Pick<ComponentLineProps, "preview">) => {
  const options = buildOptions(preview);
  return <RawLines.HeaderLine preview={preview} options={options} />;
};
